<template>
  <v-dialog
    max-width="400"
    scrollable
  >
    <template #activator="{ on: listOn, attrs: listAttrs }">
      <v-list-item
        :disabled="disabled"
        v-bind="listAttrs"
        v-on="listOn"
      >
        <v-list-item-title>
          適用ユーザーの変更
        </v-list-item-title>
      </v-list-item>
    </template>

    <template #default="dialog">
      <v-card style="max-height: 70vh; overflow-y: auto;">
        <v-card-title>適用ユーザー管理</v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item
              v-for="(user, index) in assignedUsers"
              :key="`assigned-user-${user.id}-${index}`"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ user.attributes.name }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  icon
                  @click="removeUser(user)"
                >
                  <v-icon
                    small
                  >
                    {{ icons.mdiClose }}
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-divider class="my-4"></v-divider>

          <v-combobox
            :value="selectedUserName"
            :items="userItems"
            :item-text="item => item.attributes ? item.attributes.name : item.header || ''"
            label="新しくユーザーを紐付ける"
            placeholder="ユーザーを選択してください"
            no-data-text="利用可能なユーザーがありません"
            @change="onUserSelect"
          >
            <template #item="{ item: userItem }">
              <template v-if="!userItem.attributes">
                <v-list-item-content class="user-group-header">
                  <v-list-item-title class="text-caption">
                    {{ userItem.header }}
                  </v-list-item-title>
                </v-list-item-content>
              </template>

              <template v-else>
                <v-list-item-avatar
                  size="32"
                  class="ml-5 mr-2"
                >
                  <v-img
                    v-if="userItem.attributes.profileImage"
                    :src="userItem.attributes.profileImage"
                  />
                  <v-icon v-else>
                    {{ icons.mdiAccountCircleOutline }}
                  </v-icon>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="user-name">
                    {{ userItem.attributes.name }}
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </template>
          </v-combobox>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="
              dialog.value = false
              $emit('cancel')
            "
          >
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
import { ref, computed, nextTick } from '@vue/composition-api'
import { mdiClose, mdiAccountCircleOutline } from '@mdi/js'
import { useVModel } from '@vueuse/core'
import {
  groupBy,
  each,
  keys,
} from 'lodash'

export default {
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    assignedUsers: {
      type: Array,
      default: () => [],
    },
    availableUsers: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const assignedUsers = useVModel(props, 'assignedUsers')
    const availableUsers = useVModel(props, 'availableUsers')

    const loading = ref(false)
    const selectedUserId = ref(null)

    const selectedUserName = computed(() => {
      if (!selectedUserId.value) return ''

      return availableUsers.value.find(user => user.attributes.id === selectedUserId.value)?.attributes?.name
    })

    const userItems = computed(() => {
      const roleMsg = {
        cast: 'キャスト',
        waiter: 'ボーイ',
        alliance: 'アライアンス',
      }
      const lists = []
      const usersGrouped = groupBy(availableUsers.value, 'attributes.role')

      each(keys(roleMsg), role => {
        if (!usersGrouped[role]) return

        // 各ロールグループ内でユーザー名をあいうえお順（数字は昇順）でソート
        const sortedUsers = usersGrouped[role].sort((a, b) => {
          const nameA = a.attributes?.name || ''
          const nameB = b.attributes?.name || ''

          return nameA.localeCompare(nameB, 'ja', {
            numeric: true,
            sensitivity: 'base',
          })
        })

        lists.push({ header: roleMsg[role] })
        lists.push(...sortedUsers)
      })

      return lists
    })

    const update = async () => {
      if (loading.value) return

      loading.value = true

      const callback = () => { loading.value = false }

      emit('update', {
        users: assignedUsers.value,
        callback,
      })
    }

    const onUserSelect = item => {
      if (loading.value) return

      // ヘッダーアイテムの場合は何もしない
      if (!item?.attributes) return

      if (item?.attributes?.id) {
        selectedUserId.value = item.attributes.id
        const newUser = availableUsers.value.find(u => u.attributes.id === item.attributes.id)
        const persistedUser = assignedUsers.value.find(u => u.attributes.id === item.attributes.id)

        if (newUser && !persistedUser) {
          assignedUsers.value.push(newUser)
          availableUsers.value = availableUsers.value.filter(u => u.attributes.id !== newUser.attributes.id)
          nextTick(() => update())
        }
      }
    }

    const removeUser = user => {
      assignedUsers.value = assignedUsers.value.filter(u => u.attributes.id !== user.attributes.id)
      availableUsers.value = [...availableUsers.value, user]
      selectedUserId.value = null
      nextTick(() => update())
    }

    return {
      loading,
      selectedUserId,
      selectedUserName,
      userItems,
      onUserSelect,
      removeUser,
      icons: {
        mdiClose,
        mdiAccountCircleOutline,
      },
    }
  },
}
</script>

<style scoped>
::v-deep .user-group-header .v-list-item__title {
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0.7;
}

::v-deep .user-name {
  font-size: 0.875rem;
  font-weight: 400;
}
</style>
